import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from "../context/ThemeContext";
import { usePolling } from "../context/PollingContext";
import { useContainerSize } from "../hooks/useContainerSize";
import { useUniversalTextSizing, getUniversalTextConfig } from "../utils/cardTitleSizing";
import { CardTitle, BodyText, SmallText, CaptionText, UniversalBadge, useUniversalSizing } from "./UniversalText";
import {
FileText,
AlertCircle,
Check,
Star,
Clock,
Search,
PlayCircle,
ArrowRight,
Award,
Sparkles,
Music2,
X,
Lock,
Ban,
} from "lucide-react";
import { AWARDS_API, fetchWithAuth } from "../utils/api";

// Helper function to format scores without unnecessary decimals
const formatScore = (score: number): string => {
  // If the score is a whole number, return it without decimals
  if (score % 1 === 0) {
    return score.toString();
  }
  // Otherwise, return with one decimal place
  return score.toFixed(1);
};

// Type definitions remain the same
export interface Routine {
routineNumber: string;
name?: string;
performerName?: string;
studioCode?: string;
studioName?: string;
category?: string;
division?: string;
type?: string;
ageGroup?: string;
scratched?: boolean;
'Routine #'?: string;
[key: string]: any;
}

export interface Award {
name: string;
color: string;
minScore: number;
maxScore: number;
}

export interface RoutineScoreData {
totalScore: number;
judgeCount: number;
judges?: { [judgeId: string]: number };
}

export interface RoutineScores {
[routineId: string]: RoutineScoreData;
}

export interface FlexibleScoreData {
finalScore: number;
droppedScores: Array<{
  judgeId: string;
  totalScore: number;
  dropReason?: string;
}>;
judgeScores: Array<{
  judgeId: string;
  totalScore: number;
}>;
appliedRules: string[];
correlatedAward: string;
}

export interface JudgeSubmissions {
[routineId: string]: string[];
}

export interface ScoreSubmissionData {
judgeId: string;
routineId: string;
scores: { [criterionId: string]: number | string };
}

export interface JudgesUpdateData {
judges?: string[];
profiles?: { [judgeId: string]: any };
}

export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge';
export type ActiveTab = 'all' | 'upcoming' | 'completed' | 'top';

export interface SizeConfig {
headerSpacing: string;
titleSize: string;
iconSize: string;
buttonPadding: string;
buttonTextSize: string;
searchSpacing: string;
searchPadding: string;
searchTextSize: string;
jumpInputWidth: string;
tabGap: string;
tabPadding: string;
tabTextSize: string;
routineSpacing: string;
routinePadding: string;
routineGap: string;
numberSize: string;
nameSpacing: string;
nameTextSize: string;
tagSpacing: string;
tagPadding: string;
tagTextSize: string;
scoreSize: string; // Add prominent score size
awardPillSize: string; // Add compact award pill size
baseOffset: number;
maxHeight: string;
minHeight: string;
debugTextSize: string;
debugSpacing: string;
}

export interface PerformanceScheduleProps {
routines?: Routine[];
currentIndex?: number;
onRoutineSelect?: (index: number) => void;
onRoutinesUpload?: (routines: Routine[]) => void;
className?: string;
maxHeight?: string;
judgeId?: string; // Add judgeId prop for judge-specific score display
isTabulator?: boolean; // Add flag to identify tabulator view
isJudgeView?: boolean; // Add flag to identify judge view for hiding sensitive info
isRoutineLocked?: (routineId: string) => boolean; // Function to check if a routine is locked for this judge
}

export interface AwardsApiResponse {
awards?: Award[];
isPercentageBased?: boolean;
maxPointsPerJudge?: number;
numberOfJudges?: number;
}

// Memoized routine item component to prevent unnecessary re-renders
// Helper to get performer name from routine object (returns empty for judge views)
const getPerformerNameFromRoutine = (routine: Routine, isJudgeView: boolean = false): string => {
  // For judge views, never show performer names to keep display clean and simple
  if (isJudgeView) {
    return '';
  }
  
  if (!routine) return "";
  
  // For non-judge views, try to get performer name if available
  if (routine.performerName) return routine.performerName;
  
  // Try to get from HitchKick data structure
  const hitchkickDancers = routine.parentRoutine?.submissionRoutines?.[0]?.routineDancers;
  if (hitchkickDancers && Array.isArray(hitchkickDancers)) {
    if (hitchkickDancers.length === 1) {
      // Solo - show the dancer's name
      const dancer = hitchkickDancers[0].rosterDancers;
      if (dancer && dancer.firstName && dancer.lastName) {
        return `${dancer.firstName} ${dancer.lastName}`;
      }
    } else if (hitchkickDancers.length > 1) {
      // Group routine - show nothing (empty string)
      return '';
    }
  }
  
  // Fallback to original enhanced data structure
  if (routine.parentRoutine?.routineDancers?.length > 0) {
    const dancers = routine.parentRoutine.routineDancers;
    if (dancers.length === 1) {
      const dancer = dancers[0].rosterDancers;
      if (dancer && dancer.firstName && dancer.lastName) {
        return `${dancer.firstName} ${dancer.lastName}`;
      }
    } else if (dancers.length > 1) {
      // Group routine - show nothing (empty string)
      return '';
    }
  }
  
  // Return empty string
  return '';
};

const RoutineItem = memo<{
routine: Routine;
index: number;
routineIdx: number;
isCurrentRoutine: boolean;
isFullyScored: boolean;
isPartiallyScored: boolean;
isScratched: boolean;
totalScore: number | null;
award: Award | null;
sizeConfig: SizeConfig;
isDarkMode: boolean;
connectedJudges: string[];
judgeSubmissions: JudgeSubmissions;
judgeScore: number | null; // Add judge-specific score
flexibleScoreData: FlexibleScoreData | null; // Add flexible scoring data
isTabulator: boolean; // Add tabulator flag
isJudgeView: boolean; // Add judge view flag for hiding sensitive info
isLocked: boolean; // Add locked state for this routine
onSelect: (index: number) => void;
setRef: (routineId: string, el: HTMLButtonElement | null) => void;
}>(({ 
routine, 
index, 
routineIdx, 
isCurrentRoutine, 
isFullyScored, 
isPartiallyScored, 
isScratched, 
totalScore, 
award,
sizeConfig, 
isDarkMode, 
connectedJudges, 
judgeSubmissions, 
judgeScore, // Add judge-specific score
flexibleScoreData, // Add flexible scoring data
isTabulator, // Add tabulator flag
isJudgeView, // Add judge view flag
isLocked, // Add locked state
onSelect,
setRef 
}) => {
const routineId = routine.routineNumber;
const performerName = getPerformerNameFromRoutine(routine, isJudgeView);
const hasPerformerName = performerName && performerName.trim() !== "";


// Reduce motion for better performance on weak devices
const motionProps = useMemo(() => ({
initial: { opacity: 0, y: 10 }, // Reduced from y: 20
animate: { opacity: 1, y: 0 },
transition: { delay: Math.min(index * 0.02, 0.3) }, // Reduced delay and capped
whileTap: { scale: 0.99 } // Reduced from 0.98
}), [index]);

return (
<motion.button
key={`routine-${routineId || "unknown"}-${index}`}
ref={(el) => setRef(routineId, el)}
{...motionProps}
onClick={() => !isLocked && onSelect(routineIdx)}
disabled={isLocked}
className={`w-full text-left rounded-2xl overflow-hidden transition-all duration-200 backdrop-blur-xl max-w-full ${
isScratched
  ? "bg-red-500/10 border border-red-400/30 opacity-60"
  : isLocked
  ? "bg-gray-500/10 border border-gray-400/30 opacity-50 cursor-not-allowed"
  : isCurrentRoutine
  ? "bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/50 shadow-lg shadow-pink-500/20"
  : "bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 cursor-pointer"
}`}
>
<div className={`${sizeConfig.routinePadding} w-full`}>
<div className={`flex items-center ${sizeConfig.routineGap} w-full min-w-0`}>
  {/* Left side with number */}
  <div className="flex flex-col items-center flex-shrink-0">
    {isScratched ? (
      <div className={`flex items-center justify-center ${sizeConfig.numberSize} rounded-full bg-red-500/30 border border-red-400/50 text-red-300 font-semibold relative`}>
        {routineId}
        <X size={12} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5" />
      </div>
    ) : isCurrentRoutine ? (
      <div className={`flex items-center justify-center ${sizeConfig.numberSize} rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold`}>
        {routineId}
      </div>
    ) : (
      <div className={`flex items-center justify-center ${sizeConfig.numberSize} rounded-full ${isLocked ? 'bg-gray-500/30 border border-gray-400/50 text-gray-300' : 'bg-white/20 border border-white/30 text-white/80'} font-semibold`}>
        {routineId}
      </div>
    )}
  </div>

  {/* Middle section with routine info - Optimized for score prominence */}
  <div className={`flex-1 min-w-0 overflow-hidden mr-2 ${isLocked ? 'opacity-70' : ''}`}>
    {/* Name section - Gradient only on selected routine, larger text */}
    <div className={`flex items-center gap-1 ${sizeConfig.nameSpacing} min-w-0 mb-1 ${
      isCurrentRoutine 
        ? "bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent" 
        : "text-white"
    } font-black text-xl drop-shadow-sm`}>
      {/* For tabulator: prioritize routine name (never truncate) and studio name */}
      {isTabulator ? (
        <>
          <SmallText className="flex-shrink-0 font-black">
            {routine.name}
          </SmallText>
          
          {/* Studio name is second priority - show full studio name or code */}
          {(routine.studioName || routine.studioCode) && (
            <>
              <SmallText className="flex-shrink-0 font-bold">•</SmallText>
              <SmallText className="truncate flex-shrink min-w-0" title={routine.studioName || routine.studioCode}>
                {routine.studioName || routine.studioCode}
              </SmallText>
            </>
          )}
          
          {/* Dancer/performer names are omitted entirely for tabulator to save space */}
        </>
      ) : (
        <>
          <SmallText className="truncate flex-shrink">
            {routine.name}
          </SmallText>
          
          {/* Show performer name for non-tabulator views when available */}
          {!isTabulator && hasPerformerName && (
            <>
              <SmallText className="flex-shrink-0 font-bold">({performerName})</SmallText>
            </>
          )}
          
          {/* For judges: don't show studio info to keep display clean */}
          
          {/* For non-judges: show full studio name */}
          {!isJudgeView && routine.studioName && (
            <>
              <SmallText className="flex-shrink-0 font-bold">•</SmallText>
              <SmallText className="truncate flex-shrink min-w-0" title={routine.studioName}>
                {routine.studioName}
              </SmallText>
            </>
          )}
        </>
      )}
    </div>

    {/* Tags section */}
    <div className={`flex flex-nowrap gap-0.5 ${sizeConfig.tagSpacing} overflow-hidden`}>
      {/* Age Group - First in order */}
      {routine.ageGroup && (
        <span className={`${sizeConfig.tagPadding} ${sizeConfig.tagTextSize} rounded-full flex-shrink-0 ${
          isCurrentRoutine
            ? (isDarkMode ? "bg-yellow-900/30 text-yellow-300" : "bg-yellow-100 text-yellow-800")
            : (isDarkMode ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-500")
        }`}>
          <span className="truncate">{routine.ageGroup}</span>
        </span>
      )}

      {/* Category - Second in order */}
      {routine.category && (
        <span className={`${sizeConfig.tagPadding} ${sizeConfig.tagTextSize} rounded-full flex-shrink-0 ${
          isCurrentRoutine
            ? (isDarkMode ? "bg-purple-900/30 text-purple-300" : "bg-purple-100 text-purple-800")
            : (isDarkMode ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-500")
        }`}>
          <span className="truncate">{routine.category}</span>
        </span>
      )}

      {/* Division - Third in order */}
      {routine.division && (
        <span className={`${sizeConfig.tagPadding} ${sizeConfig.tagTextSize} rounded-full flex-shrink-0 ${
          isCurrentRoutine
            ? (isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-100 text-blue-800")
            : (isDarkMode ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-500")
        }`}>
          <span className="truncate">{routine.division}</span>
        </span>
      )}

      {/* Type - Fourth in order (if needed) */}
      {routine.type && routine.type !== 'routine' && (
        <span className={`${sizeConfig.tagPadding} ${sizeConfig.tagTextSize} rounded-full flex-shrink-0 ${
          isCurrentRoutine
            ? (isDarkMode ? "bg-green-900/30 text-green-300" : "bg-green-100 text-green-800")
            : (isDarkMode ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-500")
        }`}>
          <span className="truncate">{routine.type}</span>
        </span>
      )}
    </div>
  </div>

    {/* Right side with score/status - Increased space for prominent scores */}
    <div className="flex items-center gap-3 ml-3 flex-shrink-0 min-w-[140px]">
      {/* Simple lock icon for locked routines - Judge view only */}
      {isJudgeView && isLocked && (
        <div className="flex items-center justify-center px-3">
          <Lock size={16} className="text-gray-400/80" />
        </div>
      )}
      
      
      {/* Enhanced Tabulator view with flexible scoring */}
      {(() => {
        const shouldShow = isTabulator && isFullyScored && flexibleScoreData && !isScratched && !(isJudgeView && isLocked);
        return shouldShow;
      })() && (
      <div className="flex flex-col items-center px-2 sm:px-3 min-w-[120px] sm:min-w-[140px]">
        {/* Prominent Score Display */}
        <SmallText className={`font-black ${isCurrentRoutine ? 'text-yellow-300' : 'text-white/90'} leading-none`}>
          {flexibleScoreData!.droppedScores.length > 0 ? (
            <span>
              {flexibleScoreData!.finalScore.toFixed(0)}:{flexibleScoreData!.droppedScores.map(d => d.totalScore != null ? d.totalScore.toFixed(0) : '0').join(',')}
            </span>
          ) : (
            <span>{flexibleScoreData!.finalScore.toFixed(0)}</span>
          )}
        </SmallText>
        {/* Complementary Award Pill */}
        {flexibleScoreData!.correlatedAward && (
          <div className={`${sizeConfig.awardPillSize} rounded-full bg-white/5 border border-white/10 text-white/50 font-normal flex items-center gap-0.5 backdrop-blur-sm mt-1`}>
            <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0"></div>
            <span className="truncate">{flexibleScoreData!.correlatedAward}</span>
          </div>
        )}
      </div>
    )}

      {/* Standard tabulator view (fallback when no flexible scoring data) */}
      {isTabulator && isFullyScored && !flexibleScoreData && totalScore !== null && !isScratched && !(isJudgeView && isLocked) && (
      <div className="flex flex-col items-center px-2 sm:px-3 min-w-[100px] sm:min-w-[120px]">
        {/* Prominent Score Display */}
        <SmallText className={`font-black ${isCurrentRoutine ? 'text-yellow-300' : 'text-white/90'} leading-none`}>
          {totalScore.toFixed(0)}
        </SmallText>
        {/* Complementary Award Pill */}
        {award && (
          <div className={`${sizeConfig.awardPillSize} rounded-full bg-white/5 border border-white/10 text-white/50 font-normal flex items-center gap-0.5 backdrop-blur-sm mt-1`}>
            <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0"></div>
            <span className="truncate">{award.name}</span>
          </div>
        )}
      </div>
    )}

      {/* Judge-specific score display */}
      {!isTabulator && judgeScore !== null && !isScratched && !(isJudgeView && isLocked) && (
      <div className="flex flex-col items-center px-2 sm:px-3">
        {/* Prominent Judge Score */}
        <SmallText className={`font-black ${isCurrentRoutine ? 'text-yellow-300' : 'text-white/90'} leading-none`}>
          {judgeScore}
        </SmallText>
        {/* Judge Score Label Pill */}
        <div className={`${sizeConfig.awardPillSize} rounded-full bg-white/5 border border-white/10 text-white/50 font-normal flex items-center gap-0.5 backdrop-blur-sm mt-1`}>
          <Star size={10} className="flex-shrink-0" />
          <span className="truncate">My Score</span>
        </div>
      </div>
    )}

    {/* Legacy judge view (when no specific judge ID provided) */}
    {!isTabulator && !judgeScore && isFullyScored && totalScore !== null && !isScratched && (
      <div className="flex flex-col items-center px-2 sm:px-3 min-w-[100px] sm:min-w-[120px]">
        {/* Prominent Score Display */}
        <SmallText className={`font-black ${isCurrentRoutine ? 'text-yellow-300' : 'text-white/90'} leading-none`}>
          {formatScore(totalScore)}
        </SmallText>
        {/* Complementary Award Pill */}
        {award && (
          <div className={`${sizeConfig.awardPillSize} rounded-full bg-white/5 border border-white/10 text-white/50 font-normal flex items-center gap-0.5 backdrop-blur-sm mt-1`}>
            <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0"></div>
            <span className="truncate">{award.name}</span>
          </div>
        )}
      </div>
    )}

    {isPartiallyScored && !isFullyScored && !isScratched && (
      <div className="flex flex-col items-center px-1.5 sm:px-2">
        <div className={`${sizeConfig.tagTextSize} flex items-center text-yellow-500 font-medium`}>
          <Clock size={12} className="mr-0.5 flex-shrink-0" />
          <span>{judgeSubmissions[routineId]?.length || 0}/{connectedJudges.length}</span>
        </div>
        <div className="text-xs flex items-center text-yellow-600">
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-0.5"></div>
          <span className="font-semibold">In Progress</span>
        </div>
      </div>
    )}

    {isScratched && (
      <div className="flex flex-col items-center px-1.5 sm:px-2">
        <div className={`${sizeConfig.tagTextSize} flex items-center text-red-400 font-medium`}>
          <Ban size={12} className="mr-0.5 flex-shrink-0" />
          <span>SCRATCHED</span>
        </div>
        <div className={`${sizeConfig.tagTextSize} flex items-center text-red-500`}>
          <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-0.5"></div>
          <span className="font-semibold">Withdrawn</span>
        </div>
      </div>
    )}
  </div>
</div>
</div>
</motion.button>
);
});

RoutineItem.displayName = 'RoutineItem';

// Virtual scrolling implementation for large lists
const useVirtualScrolling = (items: any[], containerHeight: number, itemHeight: number) => {
const [scrollTop, setScrollTop] = useState(0);

const visibleStart = Math.floor(scrollTop / itemHeight);
const visibleEnd = Math.min(visibleStart + Math.ceil(containerHeight / itemHeight) + 2, items.length);

const visibleItems = items.slice(visibleStart, visibleEnd);
const totalHeight = items.length * itemHeight;
const offsetY = visibleStart * itemHeight;

return {
visibleItems,
totalHeight,
offsetY,
setScrollTop
};
};

const PerformanceSchedule: React.FC<PerformanceScheduleProps> = ({
routines = [],
currentIndex = 0,
onRoutineSelect = () => {},
onRoutinesUpload,
className = "",
maxHeight,
judgeId, // Accept judgeId prop
isTabulator = false, // Accept tabulator flag
isJudgeView = false, // Accept judge view flag
isRoutineLocked, // Accept routine locking function
}) => {
const { theme } = useTheme();
const isDarkMode = theme === "dark";
const { connected, scoreUpdates } = usePolling();

// Debug logging for scoreUpdates to troubleshoot routine 987 issue
useEffect(() => {
  // Remove debug logging for production
}, [scoreUpdates]);


// Use universal text sizing for consistency
const { textSize } = useUniversalTextSizing();
const universalConfig = getUniversalTextConfig(textSize);

const [error, setError] = useState<string | null>(null);
const [routineScores, setRoutineScores] = useState<RoutineScores>({});
const [judgeSubmissions, setJudgeSubmissions] = useState<JudgeSubmissions>({});
const [connectedJudges, setConnectedJudges] = useState<string[]>([]);
const [searchQuery, setSearchQuery] = useState<string>("");
const [activeTab, setActiveTab] = useState<ActiveTab>("all");
const [jumpToNumber, setJumpToNumber] = useState<string>("");
const [awards, setAwards] = useState<Award[]>([]);
const [isPercentageBased, setIsPercentageBased] = useState<boolean>(true);
const [maxPointsPerJudge, setMaxPointsPerJudge] = useState<number>(100);
const [numberOfJudges, setNumberOfJudges] = useState<number>(3);
const containerRef = useRef<HTMLDivElement>(null);
const routinesContainerRef = useRef<HTMLDivElement>(null);
const [containerHeight, setContainerHeight] = useState<number>(0);
const routineRefs = useRef<{ [key: string]: HTMLButtonElement }>({});

// Container-responsive design
const { containerSize } = useContainerSize(containerRef);

// Determine effective container size with fallback
const effectiveContainerSize: ContainerSize = containerSize || 'small';

// Throttled search to reduce re-renders
const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

useEffect(() => {
const timer = setTimeout(() => {
setDebouncedSearchQuery(searchQuery);
}, 200); // Reduced from typical 300ms for responsiveness

return () => clearTimeout(timer);
}, [searchQuery]);

// Container-responsive size configuration (memoized)
const sizeConfig = useMemo((): SizeConfig => {
const configs: Record<ContainerSize, SizeConfig> = {
small: {
headerSpacing: 'mb-1',
titleSize: universalConfig.cardTitle,
iconSize: 'w-3 h-3',
buttonPadding: 'px-2 py-1',
buttonTextSize: universalConfig.captionText,
searchSpacing: 'mb-1',
searchPadding: 'py-1.5',
searchTextSize: universalConfig.captionText,
jumpInputWidth: 'w-16',
tabGap: 'gap-1',
tabPadding: 'px-2 py-1',
tabTextSize: universalConfig.captionText,
routineSpacing: 'space-y-1',
routinePadding: 'p-1.5',
routineGap: 'gap-1.5',
numberSize: 'w-6 h-6 text-xs',
nameSpacing: 'mb-0.5',
nameTextSize: universalConfig.smallText,
tagSpacing: 'mt-0.5',
tagPadding: 'px-1.5 py-0.5',
tagTextSize: universalConfig.captionText,
scoreSize: 'text-xl font-black', // Match routine name size and weight
awardPillSize: 'text-xs px-1.5 py-0.5', // Match routine info tags exactly
baseOffset: 30,
maxHeight: '98vh',
minHeight: '400px',
debugTextSize: universalConfig.captionText,
debugSpacing: 'mb-1 px-2'
},
// ... other sizes remain the same
medium: {
headerSpacing: 'mb-1',
titleSize: universalConfig.cardTitle,
iconSize: 'w-3 h-3',
buttonPadding: 'px-2 py-1',
buttonTextSize: universalConfig.captionText,
searchSpacing: 'mb-1',
searchPadding: 'py-1.5',
searchTextSize: universalConfig.captionText,
jumpInputWidth: 'w-16',
tabGap: 'gap-1',
tabPadding: 'px-2 py-1',
tabTextSize: universalConfig.captionText,
routineSpacing: 'space-y-1',
routinePadding: 'p-1.5',
routineGap: 'gap-1.5',
numberSize: 'w-6 h-6 text-xs',
nameSpacing: 'mb-0.5',
nameTextSize: universalConfig.smallText,
tagSpacing: 'mt-0.5',
tagPadding: 'px-2 py-0.5',
tagTextSize: universalConfig.captionText,
scoreSize: 'text-xl font-black', // Match routine name size and weight
awardPillSize: 'text-xs px-2 py-0.5', // Match routine info tags
baseOffset: 40,
maxHeight: '98vh',
minHeight: '400px',
debugTextSize: universalConfig.captionText,
debugSpacing: 'mb-1 px-2'
},
large: {
headerSpacing: 'mb-1',
titleSize: universalConfig.cardTitle,
iconSize: 'w-4 h-4',
buttonPadding: 'px-2.5 py-1',
buttonTextSize: universalConfig.captionText,
searchSpacing: 'mb-1',
searchPadding: 'py-1.5',
searchTextSize: universalConfig.captionText,
jumpInputWidth: 'w-18',
tabGap: 'gap-1',
tabPadding: 'px-2.5 py-1',
tabTextSize: universalConfig.captionText,
routineSpacing: 'space-y-1.5',
routinePadding: 'p-2',
routineGap: 'gap-2',
numberSize: 'w-7 h-7 text-xs',
nameSpacing: 'mb-0.5',
nameTextSize: universalConfig.smallText,
tagSpacing: 'mt-0.5',
tagPadding: 'px-2.5 py-0.5',
tagTextSize: universalConfig.captionText,
scoreSize: 'text-xl font-black', // Match routine name size and weight
awardPillSize: 'text-xs px-2.5 py-0.5', // Match routine info tags
baseOffset: 50,
maxHeight: '98vh',
minHeight: '400px',
debugTextSize: universalConfig.captionText,
debugSpacing: 'mb-1 px-2'
},
xlarge: {
headerSpacing: 'mb-1',
titleSize: universalConfig.cardTitle,
iconSize: 'w-4 h-4',
buttonPadding: 'px-3 py-1',
buttonTextSize: universalConfig.captionText,
searchSpacing: 'mb-1',
searchPadding: 'py-1.5',
searchTextSize: universalConfig.captionText,
jumpInputWidth: 'w-20',
tabGap: 'gap-1.5',
tabPadding: 'px-3 py-1',
tabTextSize: universalConfig.captionText,
routineSpacing: 'space-y-1.5',
routinePadding: 'p-2',
routineGap: 'gap-2',
numberSize: 'w-7 h-7 text-sm',
nameSpacing: 'mb-0.5',
nameTextSize: universalConfig.smallText,
tagSpacing: 'mt-0.5',
tagPadding: 'px-2.5 py-0.5',
tagTextSize: universalConfig.captionText,
scoreSize: 'text-xl font-black', // Match routine name size and weight
awardPillSize: 'text-xs px-2.5 py-0.5', // Match routine info tags
baseOffset: 70,
maxHeight: '98vh',
minHeight: '400px',
debugTextSize: universalConfig.captionText,
debugSpacing: 'mb-1 px-2'
}
};
return configs[effectiveContainerSize];
}, [effectiveContainerSize, universalConfig]);

// Optimized container height measurement
useEffect(() => {
let rafId: number;

const handleResize = () => {
if (rafId) cancelAnimationFrame(rafId);

rafId = requestAnimationFrame(() => {
if (containerRef.current) {
  const rect = containerRef.current.getBoundingClientRect();
  setContainerHeight(rect.height);
}
});
};

handleResize();
window.addEventListener("resize", handleResize);
return () => {
if (rafId) cancelAnimationFrame(rafId);
window.removeEventListener("resize", handleResize);
};
}, []);

// Load awards configuration (cached)
useEffect(() => {
let isMounted = true;

const loadAwards = async () => {
try {
const data: AwardsApiResponse = await fetchWithAuth(AWARDS_API);
if (isMounted) {
  setAwards(data.awards || []);
  setIsPercentageBased(data.isPercentageBased ?? true);
  setMaxPointsPerJudge(data.maxPointsPerJudge || 100);
  setNumberOfJudges(data.numberOfJudges || 3);
}
} catch (err) {
if (isMounted) {
  console.error("Error loading awards:", err);
}
}
};

loadAwards();
return () => { isMounted = false; };
}, []);

// Memoized award calculation
const getAwardForScore = useCallback(
(score: number): Award | null => {
if (score === null || score === undefined || !awards?.length) return null;
return awards.find(award => score >= award.minScore && score <= award.maxScore) || awards[awards.length - 1];
},
[awards]
);

// Polling will handle updates automatically
// No need for WebSocket listeners

// Get judge-specific score for a routine
const getJudgeScore = useCallback(
(routineNumber: string, judgeId: string): number | null => {
if (!routineNumber || !judgeId || !scoreUpdates) return null;

// Find the routine to get its actual internal ID
const routine = routines.find(r => r.routineNumber === routineNumber);
if (!routine) return null;

// Use the internal routineId for consistent score lookup
const routineId = routine.id;
const scoreKey = `${routineId}_${judgeId}`;
const scoreData = scoreUpdates[scoreKey];

if (!scoreData || !scoreData.isSubmitted) return null;

// Return simple score if available, otherwise calculate total from scores
if (scoreData.simpleScore !== undefined) {
  return scoreData.simpleScore;
}

if (scoreData.scores && typeof scoreData.scores === 'object') {
  // Calculate total from individual scores
  const scores = Object.values(scoreData.scores);
  if (scores.length > 0 && scores.every(score => typeof score === 'number')) {
    return (scores as number[]).reduce((sum: number, score: number) => sum + score, 0);
  }
}

return null;
},
[scoreUpdates, routines]
);

// Get flexible scoring data for tabulator view
const getFlexibleScoreData = useCallback(
(routineNumber: string): FlexibleScoreData | null => {
if (!routineNumber) return null;

// Find the routine to get its actual internal ID
const routine = routines.find(r => r.routineNumber === routineNumber);
if (!routine) return null;

// Use the internal routineId for consistent score lookup
const routineId = routine.id;

// Build flexible score data from individual judge scores
if (isTabulator && scoreUpdates) {
  const judgeScores: Array<{judgeId: string; totalScore: number;}> = [];
  
  // Look for individual judge scores with pattern: routineId_judgeId
  Object.keys(scoreUpdates).forEach(key => {
    if (key.startsWith(`${routineId}_`) && !key.includes('flexible')) {
      const scoreData = scoreUpdates[key];
      if (scoreData && scoreData.isSubmitted) {
        const judgeScore = scoreData.simpleScore || 0;
        
        judgeScores.push({
          judgeId: scoreData.judgeId || key.split('_')[1],
          totalScore: judgeScore
        });
      }
    }
  });

  // Need at least 3 scores to display
  if (judgeScores.length >= 3) {
    // Sort scores from highest to lowest
    const sortedScores = [...judgeScores].sort((a, b) => b.totalScore - a.totalScore);
    
    let finalScore = 0;
    let droppedScores: Array<{judgeId: string; totalScore: number; dropReason?: string}> = [];
    let appliedRules: string[] = [];
    
    if (sortedScores.length === 3) {
      // 3 judges: sum all scores, show 0 as dropped score for consistency
      finalScore = sortedScores.reduce((sum, score) => sum + score.totalScore, 0);
      droppedScores = [{
        judgeId: 'none',
        totalScore: 0,
        dropReason: 'No 4th judge'
      }];
      appliedRules.push('3 judges: all scores counted');
    } else if (sortedScores.length >= 4) {
      // 4+ judges: sum the highest 3, drop the lowest
      const topThree = sortedScores.slice(0, 3);
      const dropped = sortedScores.slice(3);
      
      finalScore = topThree.reduce((sum, score) => sum + score.totalScore, 0);
      droppedScores = dropped.map(score => ({
        judgeId: score.judgeId,
        totalScore: score.totalScore,
        dropReason: 'Lowest score dropped'
      }));
      appliedRules.push(`${sortedScores.length} judges: highest 3 scores counted, lowest ${dropped.length} dropped`);
    }
    
    // Get correlated award based on final score
    const award = getAwardForScore(finalScore);
    const correlatedAward = award ? award.name : 'No Award';
    
    return {
      finalScore,
      judgeScores: sortedScores,
      droppedScores,
      appliedRules,
      correlatedAward
    };
  }
}

// No demo data - return null if no actual data available
return null;
},
[scoreUpdates, isTabulator, routines, getAwardForScore]
);

// Memoized helper functions
const isRoutineFullyScored = useCallback(
  (routineId: string): boolean => {
    if (!routineId) return false;

    // For tabulator view, check if flexible scoring data exists
    if (isTabulator) {
      const flexibleData = getFlexibleScoreData(routineId);
      return flexibleData !== null;
    }

    // For judge view, check if minimum required judges have submitted
    if (!connectedJudges.length) return false;
    const submittedJudges = judgeSubmissions[routineId] || [];
    // Use numberOfJudges as the minimum required (matches backend logic)
    return submittedJudges.length >= numberOfJudges && submittedJudges.length > 0;
  },
  [judgeSubmissions, connectedJudges, numberOfJudges, isTabulator, getFlexibleScoreData]
);

const isRoutinePartiallyScored = useCallback(
  (routineId: string): boolean => {
    if (!routineId) return false;
    const submittedJudges = judgeSubmissions[routineId] || [];
    // Consider it partially scored if some judges have submitted but not the minimum required
    return submittedJudges.length > 0 && submittedJudges.length < numberOfJudges;
  },
  [judgeSubmissions, numberOfJudges]
);

const getRoutineScore = useCallback(
(routineId: string): number | null => {
return routineScores[routineId]?.totalScore || null;
},
[routineScores]
);

// Optimized filtering with debounced search
const filteredRoutines = useMemo((): Routine[] => {
if (!routines || !Array.isArray(routines)) return [];

let filtered = [...routines];

// Apply search filter with debounced query
if (debouncedSearchQuery) {
const query = debouncedSearchQuery.toLowerCase();
filtered = filtered.filter(routine =>
(routine.name && routine.name.toLowerCase().includes(query)) ||
(routine.performerName && routine.performerName.toLowerCase().includes(query)) ||
(routine.routineNumber && routine.routineNumber.toString().includes(query)) ||
(routine.studioCode && routine.studioCode.toLowerCase().includes(query)) ||
(routine.studioName && routine.studioName.toLowerCase().includes(query))
);
}

// Apply tab filter
switch (activeTab) {
case "completed":
filtered = filtered.filter(routine => isRoutineFullyScored(routine.routineNumber));
break;
case "upcoming":
filtered = filtered.filter(routine => !isRoutineFullyScored(routine.routineNumber));
break;
case "top":
const currentRoutine = routines[currentIndex];
if (currentRoutine) {
  filtered = filtered
    .filter(routine => {
      if (!isRoutineFullyScored(routine.routineNumber)) return false;
      return routine.category === currentRoutine.category &&
             routine.division === currentRoutine.division &&
             routine.type === currentRoutine.type &&
             routine.ageGroup === currentRoutine.ageGroup;
    })
    .sort((a, b) => (getRoutineScore(b.routineNumber) || 0) - (getRoutineScore(a.routineNumber) || 0))
    .slice(0, 10);
} else {
  filtered = filtered
    .filter(routine => isRoutineFullyScored(routine.routineNumber))
    .sort((a, b) => (getRoutineScore(b.routineNumber) || 0) - (getRoutineScore(a.routineNumber) || 0))
    .slice(0, 10);
}
break;
}

return filtered;
}, [routines, debouncedSearchQuery, activeTab, isRoutineFullyScored, getRoutineScore, currentIndex]);

// Ref management functions
const setRoutineRef = useCallback((routineId: string, el: HTMLButtonElement | null) => {
if (el && routineId) {
routineRefs.current[routineId] = el;
}
}, []);

// Simple scroll function that centers the routine within its scrollable container
const scrollToRoutine = useCallback((routineIndex: number) => {
const routine = routines[routineIndex];
const routineElement = routine && routineRefs.current[routine.routineNumber];
const container = routinesContainerRef.current;
const outerCard = containerRef.current;

if (routineElement && container && outerCard) {
// Get the routine's position within the scrollable content
const routineOffsetTop = routineElement.offsetTop;
const routineHeight = routineElement.offsetHeight;
const containerHeight = container.clientHeight;
const cardHeight = outerCard.clientHeight;

// Calculate scroll position using the StageCard container as the visual constraint
// But account for the headers/search area by using the container's position relative to the StageCard
const stageCard = outerCard.parentElement?.parentElement;
const containerRect = container.getBoundingClientRect();
const stageCardRect = stageCard?.getBoundingClientRect();

let effectiveHeight: number;
let effectiveOffset: number;

if (stageCard && stageCardRect) {
  // Calculate the visible height within the StageCard for the scrollable area
  const containerTopRelativeToStageCard = containerRect.top - stageCardRect.top;
  const availableHeightInStageCard = stageCardRect.height - containerTopRelativeToStageCard;
  effectiveHeight = Math.min(availableHeightInStageCard, containerRect.height);
  effectiveOffset = effectiveHeight / 2;
} else {
  // Fallback to container height
  effectiveHeight = containerRect.height;
  effectiveOffset = effectiveHeight / 2;
}

const targetScrollTop = routineOffsetTop + (routineHeight / 2) - effectiveOffset;

// Ensure we don't scroll beyond the container's limits
const maxScrollTop = container.scrollHeight - containerHeight;
const clampedScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

// Smooth scroll within the container only
container.scrollTo({
  top: clampedScrollTop,
  behavior: 'smooth'
});
}
}, [routines]);

// Enhanced routine selection with auto-scroll
const handleRoutineSelect = useCallback((routineIndex: number) => {
onRoutineSelect(routineIndex);
setTimeout(() => scrollToRoutine(routineIndex), 100);
}, [onRoutineSelect, scrollToRoutine]);

const handleJumpToNumber = useCallback(() => {
if (!jumpToNumber) return;

const routineIndex = routines.findIndex((routine) => {
const routineNum = routine.routineNumber || routine['Routine #'] || (routines.indexOf(routine) + 1);
return routineNum.toString() === jumpToNumber.toString();
});

if (routineIndex >= 0) {
handleRoutineSelect(routineIndex);
} else {
setError(`Routine #${jumpToNumber} not found`);
setTimeout(() => setError(null), 3000);
}

setJumpToNumber("");
}, [jumpToNumber, routines, handleRoutineSelect]);

// Auto-scroll is now only handled by handleRoutineSelect for user-initiated actions
// Removed auto-scroll effect that was causing unwanted scrolling during normal browsing

// Error auto-clear
useEffect(() => {
if (error) {
const timeoutId = setTimeout(() => setError(null), 3000);
return () => clearTimeout(timeoutId);
}
}, [error]);

return (
<div ref={containerRef} className={`performance-schedule-card flex flex-col h-full overflow-hidden ${className}`}>
      {/* Fixed header section - sticky at top with no background */}
      <div className={`schedule-header sticky top-0 z-20 flex items-center flex-shrink-0 px-4 py-1 -mx-4 -mt-4`}>
        <div>
          <CardTitle className="flex items-center gap-2">
            <Music2 className={`${universalConfig.iconSize} text-pink-400`} />
            PERFORMANCE SCHEDULE
          </CardTitle>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/40 text-red-200 flex items-center gap-2 flex-shrink-0 backdrop-blur-xl">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span className={`${sizeConfig.buttonTextSize} font-medium`}>{error}</span>
        </div>
      )}

      {!Array.isArray(routines) || routines.length === 0 ? (
        <div className="border-2 border-dashed rounded-2xl p-8 text-center backdrop-blur-xl border-white/20 bg-white/5">
          <div className="mx-auto mb-4">
            <FileText size={40} className="text-purple-400" />
          </div>
          <p className="text-white/80 mb-2 font-medium">
            No routines available
          </p>
          <p className={`text-white/60 ${sizeConfig.buttonTextSize}`}>
            Routines will appear here when loaded
          </p>
        </div>
      ) : (
        <>
          {/* Fixed search/filter section - sticky below header */}
          <div className={`search-filter-container sticky top-12 z-10 flex-shrink-0 py-2 mb-8`}>
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-purple-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search routines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full pl-10 pr-4 ${sizeConfig.searchPadding} ${sizeConfig.searchTextSize} rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder-white/60 focus:border-purple-400/50 focus:outline-none transition-all duration-200`}
                />
              </div>

              <div className="flex">
                <input
                  type="number"
                  placeholder="Jump to #"
                  value={jumpToNumber}
                  onChange={(e) => setJumpToNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleJumpToNumber()}
                  className={`${sizeConfig.jumpInputWidth} px-2 ${sizeConfig.searchPadding} ${sizeConfig.searchTextSize} rounded-l-xl bg-white/10 backdrop-blur-xl border border-white/20 text-white placeholder-white/60 focus:border-orange-400/50 focus:outline-none transition-all duration-200`}
                />
                <button
                  onClick={handleJumpToNumber}
                  className={`px-2 ${sizeConfig.searchPadding} rounded-r-xl flex items-center bg-gradient-to-r from-orange-500/30 to-pink-500/30 border border-orange-400/50 text-orange-200 hover:from-orange-500/40 hover:to-pink-500/40 transition-all duration-200 backdrop-blur-xl`}
                >
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Tabs - no animations, instant display */}
            <div className={`flex flex-wrap ${sizeConfig.tabGap}`}>
              {(['all', 'upcoming', 'completed', 'top'] as ActiveTab[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`${sizeConfig.tabPadding} ${sizeConfig.tabTextSize} rounded-full font-semibold transition-all duration-200 backdrop-blur-xl ${
                    activeTab === tab
                      ? tab === 'all' 
                        ? "bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-400/50 text-blue-200"
                        : tab === 'upcoming'
                        ? "bg-gradient-to-r from-purple-500/30 to-pink-500/30 border border-purple-400/50 text-purple-200"
                        : tab === 'completed'
                        ? "bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200"
                        : "bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border border-yellow-400/50 text-yellow-200"
                      : "bg-white/10 border border-white/20 text-white/70 hover:text-white hover:bg-white/20"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable routines container - accounts for fixed headers */}
          <div 
            ref={routinesContainerRef}
            className={`routines-container ${sizeConfig.routineSpacing} overflow-y-auto overflow-x-hidden pr-1`}
            style={{ 
              height: (() => {
                // If maxHeight is provided, calculate available space for scrollable content
                if (maxHeight) {
                  // For viewport units like "40vh", subtract fixed space for headers
                  if (maxHeight.includes('vh')) {
                    return `calc(${maxHeight} - 170px)`;
                  }
                  // For other units, use as-is minus headers
                  return `calc(${maxHeight} - 170px)`;
                }
                
                // Account for fixed header (~40px), search section (~90px), and margins (~40px)
                const fixedHeadersOffset = 170;
                const adjustedOffset = Math.max(sizeConfig.baseOffset + fixedHeadersOffset, fixedHeadersOffset);
                
                if (containerHeight > adjustedOffset) {
                  return `${containerHeight - adjustedOffset}px`;
                }
                
                // Fallback heights accounting for fixed headers and margins
                const fallbackHeights: Record<ContainerSize, string> = {
                  small: 'calc(90vh - 170px)',
                  medium: 'calc(92vh - 170px)',
                  large: 'calc(94vh - 170px)',
                  xlarge: 'calc(95vh - 170px)'
                };
                
                return fallbackHeights[effectiveContainerSize] || 'calc(92vh - 170px)';
              })(),
              maxHeight: maxHeight || 'calc(98vh - 170px)',
              minHeight: 'calc(' + sizeConfig.minHeight + ' - 170px)',
              // Force hardware acceleration and optimize scrolling
              transform: 'translateZ(0)',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {filteredRoutines.length === 0 ? (
              <div className="text-center p-8 text-white/60">
                <div className="mb-4">
                  <Search className="w-12 h-12 mx-auto text-purple-400" />
                </div>
                No routines match your filter criteria
              </div>
            ) : (
              // Compute routine properties for rendering
              filteredRoutines.map((routine, index) => {
                // Find the original index of this routine in the full routines array
                const routineIdx = routines.findIndex(r => r.routineNumber === routine.routineNumber);
                
                // Compute routine properties
                const isFullyScored = isRoutineFullyScored(routine.routineNumber);
                const isPartiallyScored = isRoutinePartiallyScored(routine.routineNumber);
                const totalScore = getRoutineScore(routine.routineNumber);
                const award = totalScore !== null ? getAwardForScore(totalScore) : null;
                const isScratched = routine.scratched || false;
                const isCurrentRoutine = routineIdx === currentIndex;
                const judgeSpecificScore = judgeId ? getJudgeScore(routine.routineNumber, judgeId) : null;

                const flexibleScoreData = isTabulator ? getFlexibleScoreData(routine.routineNumber) : null;
                
                // Check if routine is locked for this judge - use same ID logic as JudgeStationPage
                const routineIdForLocking = routine.number || routine.id || routine.routineNumber;
                const routineLocked = isRoutineLocked ? isRoutineLocked(String(routineIdForLocking)) : false;
                
                return (
                  <RoutineItem
                    key={`routine-${routine.routineNumber || "unknown"}-${index}`}
                    routine={routine}
                    index={index}
                    routineIdx={routineIdx}
                    isCurrentRoutine={isCurrentRoutine}
                    isFullyScored={isFullyScored}
                    isPartiallyScored={isPartiallyScored}
                    isScratched={isScratched}
                    totalScore={totalScore}
                    award={award}
                    sizeConfig={sizeConfig}
                    isDarkMode={isDarkMode}
                    connectedJudges={connectedJudges}
                    judgeSubmissions={judgeSubmissions}
                    judgeScore={judgeSpecificScore}
                    flexibleScoreData={flexibleScoreData}
                    isTabulator={isTabulator}
                    isJudgeView={isJudgeView}
                    isLocked={routineLocked}
                    onSelect={handleRoutineSelect}
                    setRef={setRoutineRef}
                  />
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(PerformanceSchedule);